import React, { FormEvent, useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { Button } from 'primereact/button'

import { Header } from '../../../lib/header'
import { Alert } from '../../../lib/alert'
import Text from '../../../ui/Text'
import updateNfcTagsForCurrentUser, { Tag } from '../../../lib/updateNfcTagsForCurrentUser'
import loadLatestRejectedNfcForUser from '../../../lib/loadLatestRejectedNfcForUser'
import useDefer, { Status } from '../../../common/hooks/useDefer'
import { WidgetLayout } from '../../../lib/widget-layout'
import loadNfcTagsForCurrentUser from '../../../lib/loadNfcTagsForCurrentUser'
import useText from '../../../common/hooks/useText'
import ValidationResult from '../../../validationSchemas/validationResult'

const SContainer = styled(WidgetLayout)`
    width: 100%;
`

const SForm = styled.form`
    display: flex;
    flex-direction: column;
`

const SRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 20px;

    &.alignSelf {
        align-self: flex-end;
        margin-bottom: 0;
    }
`

const SInput = styled.input`
    width: 175px;
    height: 30px;
    padding: 5px 10px;
    font-size: 12px;
    line-height: 1.5;
    border-radius: 3px;
`

export type UserProfileRfidTagsWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    rfid1?: { tagId: string; tagComment: string }
    rfid2?: { tagId: string; tagComment: string }
    rfid3?: { tagId: string; tagComment: string }
}

export function UserProfileRfidTagsWidget({
    className,
    style,
    rfid1,
    rfid2,
    rfid3,
}: UserProfileRfidTagsWidgetProps): React.ReactElement {
    const text = useText()

    const validateHexCode = (hex: string): ValidationResult => {
        if (![8, 14, 20].includes(hex.length)) {
            return {
                isValid: false,
                error: text('msgRFIDNotAccepted'),
            }
        }

        const isValid = /^[0-9A-Fa-f]+$/g.test(hex)
        return {
            isValid,
            error: !isValid && text('msgInvalidHexadecimal'),
        }
    }

    const [rfidOne, setRfidOne] = useState<Tag>({ id: '', comment: '' })
    const [rfidTwo, setRfidTwo] = useState<Tag>({ id: '', comment: '' })
    const [rfidThree, setRfidThree] = useState<Tag>({ id: '', comment: '' })
    const [rejectedTag, setRejectedTag] = useState<string | null | undefined>(null)

    const [tagMessage, setTagMessage] = useState<{
        type: 'success' | 'error' | null
        message: string | null
    }>({ type: null, message: null })
    const [tagError, setTagError] = useState<Array<{ type: string | undefined; message: string | undefined }>>([])

    useEffect(() => {
        setRfidOne({ id: rfid1?.tagId ?? '', comment: rfid1?.tagComment ?? '' })
        setRfidTwo({ id: rfid2?.tagId ?? '', comment: rfid2?.tagComment ?? '' })
        setRfidThree({ id: rfid3?.tagId ?? '', comment: rfid3?.tagComment ?? '' })
    }, [rfid1, rfid2, rfid3])

    const loadRejectedTagsForUser = useDefer(
        () => loadLatestRejectedNfcForUser().catch(() => Promise.reject('msgInstallationWizardExitWizardErrorHeading')),
        [],
        []
    )

    useEffect(() => {
        if (loadRejectedTagsForUser.status === Status.SUCCESS) {
            setRejectedTag(loadRejectedTagsForUser.value)
        }
    }, [loadRejectedTagsForUser.status])

    const updateTagsForUser = useDefer((tags: Tag[]) =>
        updateNfcTagsForCurrentUser(tags).catch(() => Promise.reject(text('msgRFIDTagsNotUpdated')))
    )

    useEffect(() => {
        if (updateTagsForUser.status === Status.SUCCESS) {
            setTagMessage({
                type: 'success',
                message: text('msgRFIDTagsUpdated'),
            })
            setTimeout(() => setTagMessage({ type: null, message: null }), 3000)
            loadRejectedTagsForUser.execute().catch(() => {})
            getNfcTags.execute().catch(() => {})
        }
        if (updateTagsForUser.status === Status.ERROR) {
            setTagMessage({ type: 'error', message: text('msgRFIDTagsNotUpdated') })
            setTimeout(() => setTagMessage({ type: null, message: null }), 3000)
        }
    }, [updateTagsForUser.status])

    /**Get tags **/
    const getNfcTags = useDefer(() => loadNfcTagsForCurrentUser().catch(() => Promise.reject()))

    useEffect(() => {
        if (getNfcTags.status === Status.SUCCESS) {
            const tags = getNfcTags.value ?? []

            setRfidOne({ id: tags[0]?.id ?? '', comment: tags[0]?.comment ?? '' })
            setRfidTwo({ id: tags[1]?.id ?? '', comment: tags[1]?.comment ?? '' })
            setRfidThree({ id: tags[2]?.id ?? '', comment: tags[2]?.comment ?? '' })
        }
    }, [getNfcTags.status])

    /************/
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const errors = []
        if (rfidOne.id.length > 0) {
            const { isValid, error } = validateHexCode(rfidOne.id)
            if (!isValid) errors.push({ type: rfidOne.id, message: error.toString() })
        }

        if (rfidTwo.id.length > 0) {
            const { isValid, error } = validateHexCode(rfidTwo.id)
            if (!isValid) errors.push({ type: rfidTwo.id, message: error.toString() })
        }

        if (rfidThree.id.length > 0) {
            const { isValid, error } = validateHexCode(rfidThree.id)
            if (!isValid) errors.push({ type: rfidThree.id, message: error.toString() })
        }

        if (errors.length) {
            setTagError(errors)
            return
        }

        const nfcTags = [rfidThree, rfidTwo, rfidOne]
        updateTagsForUser.execute(nfcTags).catch(() => {})
    }

    return (
        <SContainer
            className={cn(className)}
            style={style}
            title={<Header code="lblRFIDWidget" dataTestId="rfidHeader" />}
            data-testid={'rfidContainer'}
        >
            {tagMessage.message && (
                <Alert message={tagMessage.message} error={tagMessage.type === 'error' ? true : false} />
            )}
            <SForm onSubmit={handleSubmit}>
                {tagError.find((t) => t.type === rfidOne.id) && (
                    <Alert message={tagError.find((t) => t.type === rfidOne.id)?.message ?? ''} error />
                )}
                <SRow>
                    <SInput
                        className="form-control"
                        placeholder={text('lblRFIDTagId')}
                        type="text"
                        maxLength={100}
                        value={rfidOne.id}
                        onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value
                            setRfidOne({ ...rfidOne, id: value })
                        }}
                        data-testid="tag1"
                    />
                    <SInput
                        className="form-control"
                        placeholder={text('lblCommentForTagOneField')}
                        type="text"
                        maxLength={100}
                        value={rfidOne.comment}
                        onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value
                            setRfidOne({ ...rfidOne, comment: value })
                        }}
                        data-testid="comment1"
                    />
                </SRow>
                {tagError.find((t) => t.type === rfidTwo.id) && (
                    <Alert message={tagError.find((t) => t.type === rfidTwo.id)?.message ?? ''} error />
                )}
                <SRow>
                    <SInput
                        className="form-control"
                        placeholder={text('lblRFIDTagId')}
                        type="text"
                        maxLength={100}
                        value={rfidTwo.id}
                        onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value
                            setRfidTwo({ ...rfidTwo, id: value })
                        }}
                        data-testid="tag2"
                    />
                    <SInput
                        className="form-control"
                        placeholder={text('lblCommentForTagTwoField')}
                        type="text"
                        maxLength={100}
                        value={rfidTwo.comment}
                        onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value
                            setRfidTwo({ ...rfidTwo, comment: value })
                        }}
                        data-testid="comment2"
                    />
                </SRow>
                {tagError.find((t) => t.type === rfidThree.id) && (
                    <Alert message={tagError.find((t) => t.type === rfidThree.id)?.message ?? ''} error />
                )}
                <SRow>
                    <SInput
                        className="form-control"
                        placeholder={text('lblRFIDTagId')}
                        type="text"
                        maxLength={100}
                        value={rfidThree.id}
                        onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value
                            setRfidThree({ ...rfidThree, id: value })
                        }}
                        data-testid="tag3"
                    />
                    <SInput
                        className="form-control"
                        placeholder={text('lblCommentForTagThreeField')}
                        type="text"
                        maxLength={100}
                        value={rfidThree.comment}
                        onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value
                            setRfidThree({ ...rfidThree, comment: value })
                        }}
                        data-testid="comment3"
                    />
                </SRow>
                <SRow className={cn({ alignSelf: true })}>
                    <Button
                        type="submit"
                        label={text('btnSave')}
                        icon="pi pi-save"
                        loading={updateTagsForUser.status === Status.PENDING}
                        disabled={updateTagsForUser.status === Status.PENDING}
                        data-testid="sumbitBtn"
                    />
                </SRow>
                {rejectedTag && <Text code="lblLastRejectedTag" values={{ latestRejected: rejectedTag }} />}
            </SForm>
        </SContainer>
    )
}
